import { Fragment, type Node as PMNode } from "prosemirror-model";
import type { ProseMirrorDoc } from "../types.js";
import { screenplaySchema } from "./schema.js";

function cleanNode(node: PMNode): PMNode | null {
  if (node.isText) {
    const revision = node.marks.find((mark) => mark.type.name === "revision");
    if (revision?.attrs.kind === "delete") return null;
    const marks = node.marks.filter((mark) => mark.type.name !== "revision");
    return node.mark(marks);
  }
  if (node.isLeaf) return node;
  const children: PMNode[] = [];
  node.forEach((child) => {
    const cleaned = cleanNode(child);
    if (cleaned) children.push(cleaned);
  });
  return node.copy(Fragment.fromArray(children));
}

/**
 * Returns the "accepted" view of a document: inserted text remains, deleted
 * text is removed, revision marks are stripped. Used for export and stats;
 * the editor document itself is never mutated.
 */
export function acceptAllRevisions(doc: ProseMirrorDoc): ProseMirrorDoc {
  try {
    const node = screenplaySchema.nodeFromJSON(doc);
    return (cleanNode(node) ?? node).toJSON() as ProseMirrorDoc;
  } catch {
    return doc;
  }
}

export function countRevisions(doc: ProseMirrorDoc): { insertions: number; deletions: number } {
  try {
    const node = screenplaySchema.nodeFromJSON(doc);
    let insertions = 0;
    let deletions = 0;
    node.descendants((child) => {
      if (!child.isText) return;
      const revision = child.marks.find((mark) => mark.type.name === "revision");
      if (revision?.attrs.kind === "insert") insertions += child.text?.length ?? 0;
      if (revision?.attrs.kind === "delete") deletions += child.text?.length ?? 0;
    });
    return { insertions, deletions };
  } catch {
    return { insertions: 0, deletions: 0 };
  }
}
